import React, { useState } from 'react';
import { Timer, Check, X as XIcon, Clock } from '@phosphor-icons/react';

// Parse a user-friendly hours input. Accepts: "2.5", "1h 30m", "90m", "1.5h".
// Returns a non-negative float or null when un-parseable.
export function parseHours(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const s = String(raw).trim().toLowerCase();
  const hm = s.match(/^(\d+(?:\.\d+)?)\s*h(?:\s*(\d+)\s*m)?$/);
  if (hm) {
    const h = Number(hm[1]);
    const m = Number(hm[2] || 0);
    if (Number.isFinite(h) && Number.isFinite(m)) return Math.round((h + m / 60) * 100) / 100;
  }
  const mOnly = s.match(/^(\d+(?:\.\d+)?)\s*m$/);
  if (mOnly) {
    const m = Number(mOnly[1]);
    if (Number.isFinite(m)) return Math.round((m / 60) * 100) / 100;
  }
  const n = Number(s);
  if (Number.isFinite(n) && n >= 0) return Math.round(n * 100) / 100;
  return null;
}

function formatHours(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  return v % 1 === 0 ? `${v}h` : `${v.toFixed(2).replace(/\.?0+$/, '')}h`;
}

// Inline editable hours field. Click → input; blur/Enter saves; Esc cancels.
function HourInput({ value, onSave, disabled, testId, placeholder = '0h' }) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');
  const start = () => { if (disabled) return; setRaw(value === null || value === undefined ? '' : String(value)); setEditing(true); };
  const commit = async () => {
    const parsed = raw.trim() === '' ? null : parseHours(raw);
    setEditing(false);
    if (parsed === undefined) return;
    if (parsed === value) return;
    await onSave(parsed);
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input autoFocus value={raw} onChange={(e) => setRaw(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); else if (e.key === 'Escape') setEditing(false); }}
          placeholder={placeholder}
          data-testid={testId}
          className="w-20 h-7 px-2 rounded border border-blue-300 ring-2 ring-blue-100 text-[13px] font-semibold text-zinc-900 outline-none" />
        <span className="text-[10px] text-zinc-400">h / 1h 30m / 90m</span>
      </div>
    );
  }
  return (
    <button type="button" onClick={start} disabled={disabled}
      data-testid={testId}
      className={`text-[13px] font-semibold text-zinc-900 px-1.5 py-0.5 rounded ${disabled ? 'cursor-default' : 'hover:bg-zinc-100 cursor-pointer'}`}>
      {value === null || value === undefined ? <span className="text-zinc-400 font-normal">{placeholder}</span> : formatHours(value)}
    </button>
  );
}

// Compact progress bar: spent over (spent + remaining). Shows over-budget in red.
function TimeBar({ spent, remaining, estimated }) {
  const total = Math.max((spent || 0) + (remaining || 0), estimated || 0, 0.0001);
  const spentPct = Math.min(100, ((spent || 0) / total) * 100);
  const overBudget = (spent || 0) > (estimated || 0) && (estimated || 0) > 0;
  return (
    <div className="h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden" data-testid="time-bar">
      <div className={`h-full ${overBudget ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${spentPct}%` }} />
    </div>
  );
}

// Quick-log modal: accepts hours, adjusts remaining.
function QuickLogBar({ task, canEdit, saveFields }) {
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const delta = parseHours(val);
    if (!delta || delta <= 0) return;
    setSaving(true);
    const newSpent = Math.round(((task?.time_spent_hours || 0) + delta) * 100) / 100;
    const currRem = Number(task?.remaining_hours);
    const newRem = Number.isFinite(currRem) ? Math.max(0, Math.round((currRem - delta) * 100) / 100) : null;
    const patch = { time_spent_hours: newSpent };
    if (newRem !== null) patch.remaining_hours = newRem;
    try {
      await saveFields(patch);
      setVal('');
    } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2 pt-1">
      <input value={val} onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        disabled={!canEdit || saving}
        placeholder="Log hours (e.g. 2.5 or 1h 30m)"
        data-testid="log-hours-input"
        className="flex-1 h-8 px-2 rounded border border-zinc-200 text-[12px] outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
      <button type="button" onClick={submit} disabled={!canEdit || saving || !val.trim()}
        data-testid="log-hours-btn"
        className="h-8 px-3 rounded bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold disabled:opacity-40">
        {saving ? 'Logging…' : 'Log'}
      </button>
    </div>
  );
}

// Main Time block — rendered on the task detail sidebar (both TaskSheet and
// TaskDetailPage). Shows only when the project has hours_per_story_point set.
export default function TimeBlock({ task, project, canEdit, onSave }) {
  if (!project?.hours_per_story_point) return null;

  const saveFields = async (patch) => { await onSave(patch); };

  const estimate = task?.original_estimate_hours;
  const spent = task?.time_spent_hours || 0;
  const remaining = task?.remaining_hours;

  return (
    <div className="space-y-2 p-3 rounded-lg border border-zinc-200 bg-zinc-50/50" data-testid="time-block">
      <div className="flex items-center gap-1.5">
        <Timer size={14} className="text-zinc-500" weight="bold" />
        <span className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-bold">Time tracking</span>
        <span className="ml-auto text-[10px] text-zinc-400">{project.hours_per_story_point}h / SP</span>
      </div>

      <TimeBar spent={spent} remaining={remaining} estimated={estimate} />

      <div className="grid grid-cols-3 gap-2 pt-1">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Estimate</span>
          <HourInput value={estimate} disabled={!canEdit} testId="estimate-hours"
            placeholder="—"
            onSave={(v) => saveFields({ original_estimate_hours: v })} />
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Spent</span>
          <HourInput value={spent} disabled={!canEdit} testId="spent-hours"
            placeholder="0h"
            onSave={(v) => saveFields({ time_spent_hours: v === null ? 0 : v })} />
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Remaining</span>
          <HourInput value={remaining} disabled={!canEdit} testId="remaining-hours"
            placeholder="—"
            onSave={(v) => saveFields({ remaining_hours: v })} />
        </div>
      </div>

      {canEdit && <QuickLogBar task={task} canEdit={canEdit} saveFields={saveFields} />}
    </div>
  );
}
